/**
 * Manual de uso da plataforma LD Gestão — fonte única do conteúdo da página Ajuda (`/ajuda`)
 * e do arquivo `docs/MANUAL.md` (gerado por `pnpm --filter @ldembalagens/platform docs:manual`).
 *
 * Sem dependências de React ou aliases de caminho para que o script de geração consiga importar.
 */

export interface ManualTopic {
  title: string;
  /** Parágrafos explicativos. */
  text?: string[];
  /** Passo a passo numerado. */
  steps?: string[];
  /** Observações, regras e dicas. */
  notes?: string[];
}

export interface ManualSection {
  /** Identificador estável usado como âncora (`/ajuda#id`). */
  id: string;
  title: string;
  /** Rota do módulo na plataforma (para o atalho "Abrir módulo"). */
  route?: string;
  /** Permissão necessária para ver o módulo (informativa). */
  permission?: string;
  intro: string;
  topics: ManualTopic[];
}

export const MANUAL_VERSION = '2026-10';

export const MANUAL_SECTIONS: ManualSection[] = [
  {
    id: 'primeiros-passos',
    title: 'Primeiros passos',
    intro:
      'O LD Gestão é o sistema de gestão da LD Embalagens: cadastros, estoque, produção, vendas, compras, financeiro, RH, patrimônio, manutenção, fiscal, logística, relatórios e site institucional em um só lugar. Cada pessoa vê apenas os módulos que o seu perfil permite.',
    topics: [
      {
        title: 'Acessar a plataforma',
        steps: [
          'Abra o endereço da plataforma no navegador (computador, tablet ou celular).',
          'Informe seu e-mail corporativo e a senha cadastrada pelo administrador.',
          'Se você participa de mais de uma empresa, escolha a empresa ativa no seletor do cabeçalho.',
        ],
        notes: [
          'Esqueceu a senha? Solicite a redefinição ao administrador da sua empresa em Configurações › Usuários.',
          'A sessão é renovada automaticamente enquanto você usa o sistema; ao sair, use o menu do usuário › Sair.',
        ],
      },
      {
        title: 'Entender a tela',
        text: [
          'À esquerda fica a barra de módulos: cada ícone, com o nome do módulo logo abaixo, abre o módulo. Na base da barra ficam Configurações e o seu perfil. Logo abaixo do cabeçalho, as abas mostram as seções do módulo (por exemplo, em Financeiro: Visão geral, Entradas, Saídas, Contas a pagar…).',
          'O cabeçalho mostra a empresa ativa (com o seletor, se você tiver acesso a mais de uma), a busca global, o assistente, o botão Suporte (este manual e contatos), o sino de alertas, o tema claro/escuro e o menu do usuário.',
          'A busca global (atalho Ctrl+K, ou ⌘K no Mac) encontra páginas, ações rápidas (Novo produto, Novo pedido…) e, a partir de 2 letras, produtos, clientes, fornecedores e pedidos de venda. Use as setas e Enter para abrir.',
          'Em cada tela, a ação principal (como Novo produto ou Nova movimentação) é o botão verde no canto superior direito. Indicadores marcados como Período usam o intervalo escolhido; os marcados como Atual mostram o saldo de hoje.',
          'Administradores têm, no canto esquerdo do cabeçalho, o seletor de área de trabalho (Operacional, Financeiro, RH, Comercial, Compras, Administrativo ou Todos os módulos): ele recorta a barra de módulos para mostrar só o que interessa àquela função, mantendo sempre o Dashboard e as Configurações. A escolha fica salva no navegador. A busca global continua encontrando todos os módulos; ao abrir um módulo de outra área, o seletor muda para ela. No celular, o seletor fica no topo de Mais.',
          'No celular, a barra inferior leva a Início, Estoque e Vendas; Mais abre todos os outros módulos. As tabelas viram listas de cartões, os filtros ficam no botão Filtros e a ação principal fica fixa no rodapé.',
        ],
      },
      {
        title: 'Listas, filtros e tabelas',
        text: [
          'Todas as listas têm busca por texto, filtros por situação/período e paginação (10 a 100 por página). Clique no cabeçalho de uma coluna para ordenar. Clique em uma linha para abrir o detalhe; o menu ⋯ no fim da linha reúne as ações disponíveis para o seu perfil. Nos formulários, os erros aparecem logo abaixo de cada campo e as abas com problema mostram a quantidade de erros; Cancelar e Salvar ficam sempre visíveis no rodapé.',
          'Datas são exibidas no formato dia/mês/ano e valores em reais. Nos formulários, campos com * são obrigatórios.',
          'O botão Exportar (Compras, Contas a pagar e a receber, Impostos, Entradas, Saídas, Colaboradores, Lançamentos de RH, Produtos, Materiais, Posição de estoque e Movimentações) gera a lista com os mesmos filtros e a mesma ordenação da tela, trazendo todos os registros (até 5.000), não só a página atual: PDF com o timbre da empresa (logo, razão social, CNPJ e endereço no cabeçalho e no rodapé), que abre em nova aba para imprimir ou salvar; CSV, que abre direto no Excel; ou planilha Excel (XLSX). Toda exportação fica registrada na auditoria.',
        ],
      },
      {
        title: 'Alterar sua senha e preferências',
        steps: [
          'Abra o menu do usuário (seu nome, no canto superior direito).',
          'Escolha Alterar senha, informe a senha atual e a nova (mínimo de 10 caracteres com letras, números e símbolo).',
          'Use o botão de tema (sol/lua) para alternar entre claro, escuro e automático.',
        ],
      },
      {
        title: 'Assistente de IA',
        text: [
          'O botão ✦ no cabeçalho abre o assistente de IA (quando a empresa configurou a integração com o Google Gemini). Pergunte em linguagem natural sobre caixa, títulos, impostos, estoque, produção, vendas, compras, alertas e relatórios: ele consulta a plataforma e responde com os números reais, indicando o que consultou.',
          'O assistente só enxerga o que o seu perfil permite, não altera nenhum registro e cada pergunta fica na auditoria. Confira os valores na tela antes de decidir.',
        ],
      },
      {
        title: 'Nada é apagado',
        text: [
          'Por princípio, registros com histórico nunca são excluídos: títulos, pedidos, ordens, notas e colaboradores são cancelados, desligados ou inativados com motivo, e toda ação relevante fica na Auditoria. Só cadastros sem uso (uma categoria nunca usada, por exemplo) e notas fiscais lançadas por engano (sem estoque nem títulos) podem ser excluídos.',
        ],
      },
    ],
  },
  {
    id: 'dashboard',
    title: 'Dashboard',
    route: '/dashboard',
    permission: 'dashboard:read',
    intro:
      'Visão consolidada da empresa com filtro de período. Cada bloco só aparece para quem tem permissão de leitura no módulo de origem.',
    topics: [
      {
        title: 'O que o painel mostra',
        text: [
          'Indicadores financeiros (faturamento, recebido/pago, caixa, vencidos, a pagar/receber, resultado e evolução mensal), de produção (hoje/período, ordens abertas, em andamento e atrasadas, perdas e produção por tipo), operacionais (estoque disponível e abaixo do mínimo, pedidos pendentes, compras em aberto, equipamentos em manutenção, manutenções programadas/atrasadas, entregas pendentes, alertas) e rentabilidade por produto.',
        ],
        notes: ['Altere o período no topo do painel para comparar meses ou trimestres.'],
      },
    ],
  },
  {
    id: 'produtos',
    title: 'Produtos',
    route: '/produtos',
    permission: 'products:read',
    intro: 'Catálogo de paletes, caixas e embalagens fabricadas pela empresa.',
    topics: [
      {
        title: 'Cadastrar um produto',
        steps: [
          'Em Produtos, clique em Novo produto.',
          'Informe nome, SKU, categoria, unidade, dimensões, peso, madeira, tratamento e capacidade de carga.',
          'Em Preço e estoque, escolha a forma de precificação (por unidade ou por m³). Preencha custo estimado e preço: a margem é calculada automaticamente. Defina o estoque mínimo para receber alertas.',
          'Na ficha técnica (especificações) descreva o que o cliente precisa saber; marque Produto personalizado quando for sob medida.',
          'Salve. O código sequencial (PRD-000001) é gerado pelo sistema.',
        ],
        notes: [
          'Use Duplicar para criar variações rapidamente.',
          'Produtos inativos deixam de aparecer em orçamentos e pedidos, mas o histórico é preservado.',
          'O que aparece no site institucional é decidido em Site › Catálogo, não aqui.',
        ],
      },
      {
        title: 'Vender por metro cúbico (m³)',
        text: [
          'Peças de madeira (ou qualquer produto vendido por volume) podem ser precificadas por m³. Na aba Preço e estoque, escolha Vender › Por m³ e informe o preço por m³. O valor de cada item é calculado assim: volume (m³) = comprimento × largura × espessura (em mm) × quantidade ÷ 1.000.000.000; valor do item = volume × preço por m³.',
          'Marque Permitir medidas sob encomenda para cadastrar um produto base (ex.: Peça de Madeira Serrada, unidade PC) sem criar um produto para cada medida: o vendedor informa comprimento, largura e espessura em cada orçamento ou pedido. As medidas da peça no cadastro ficam como sugestão. Sem medidas sob encomenda, as medidas da peça são obrigatórias e valem em todos os documentos.',
          'Uma medida vendida com frequência pode virar uma variação (aba Variações › Nova variação): ela vira um produto com código e estoque próprios, a medida fixa e o preço por m³ do produto base (ou um preço próprio).',
        ],
        notes: [
          'O preço por m³ fica gravado em cada item do orçamento e do pedido, porque cada cliente pode ter um preço negociado diferente. Mudar o preço no cadastro não altera documentos já lançados.',
          'O preço unitário do item (usado em notas e relatórios) é derivado: valor do item ÷ quantidade.',
        ],
      },
      {
        title: 'Importar produtos por CSV',
        steps: [
          'Em Produtos, clique em Importar CSV e em Baixar modelo.',
          'Preencha uma linha por produto no Excel (Código, SKU, Nome, Descrição, Categoria, Unidade, medidas, peso, madeira, tratamento, capacidade, custo, preço de venda, estoque mínimo, sob medida, no site, situação e observações) e salve como CSV.',
          'Envie o arquivo e clique em Validar arquivo: a prévia mostra quantos produtos serão criados ou atualizados e os erros de cada linha, sem gravar nada.',
          'Clique em Importar para gravar as linhas válidas.',
        ],
        notes: [
          'Só Nome é obrigatório. Sem código, o sistema gera um; sem unidade, usa UN. A unidade precisa estar cadastrada; categorias que não existirem são criadas.',
          'Valores aceitam vírgula decimal (79,90). Sob medida e No site aceitam sim/não; Situação aceita ativo/inativo.',
          'Produto com código já cadastrado é ignorado, a menos que você marque Atualizar produtos já cadastrados: aí só as células preenchidas substituem os dados atuais.',
          'Linhas com erro não são importadas; corrija o arquivo e envie de novo. O CSV exportado da lista de produtos também pode ser reimportado.',
        ],
      },
      {
        title: 'Ficha técnica (BOM)',
        text: [
          'Na página do produto, a aba Ficha técnica define as versões de BOM: materiais por lote com perda prevista, mão de obra, tratamento e custos indiretos. É dela que as ordens de produção calculam os materiais previstos e o custo estimado por unidade.',
        ],
        notes: ['As quantidades são sempre definidas pela empresa; o sistema não presume consumo.'],
      },
    ],
  },
  {
    id: 'estoque',
    title: 'Estoque',
    route: '/estoque',
    permission: 'stock:read',
    intro:
      'Posição atual, valorização, alertas de mínimo, materiais, histórico de movimentações e inventário.',
    topics: [
      {
        title: 'Posição de estoque',
        text: [
          'A visão geral mostra saldos por depósito e por tipo de material, volume de madeira em m³, valorização a custo médio e itens abaixo do mínimo. Use os filtros para localizar um item.',
        ],
      },
      {
        title: 'Materiais',
        steps: [
          'Em Estoque › Materiais, clique em Novo material.',
          'Escolha a categoria (matéria-prima, madeira serrada, tábuas, sarrafos, blocos, pregos, insumos, consumíveis, peças…), a espécie de madeira e as dimensões em milímetros; o volume em m³ é calculado.',
          'Defina o método de custeio (médio, última compra ou padrão), estoque mínimo, local de armazenagem e fornecedor preferencial.',
        ],
      },
      {
        title: 'Registrar uma movimentação',
        steps: [
          'Em Estoque › Movimentações, clique em Nova movimentação.',
          'Escolha o tipo: entrada, saída, transferência entre depósitos, ajuste ou devolução.',
          'Informe item, depósito, quantidade e custo (nas entradas). Confirme.',
        ],
        notes: [
          'O sistema não permite saldo negativo; apenas quem tem a permissão de aprovação do estoque pode forçar uma exceção, que fica auditada.',
          'Entradas de compras, consumo de produção, expedição de entregas e peças de manutenção geram movimentos automaticamente, sempre rastreáveis até a origem.',
          'Cada movimento recebe número MOV-000001 e grava o saldo após a operação.',
        ],
      },
      {
        title: 'Inventário (contagem física)',
        steps: [
          'Em Estoque › Inventário, abra uma contagem escolhendo o depósito.',
          'Informe a quantidade contada de cada item (os demais permanecem com o saldo do sistema).',
          'Revise as diferenças e clique em Aplicar: cada diferença vira um ajuste de estoque auditado.',
        ],
      },
    ],
  },
  {
    id: 'producao',
    title: 'Produção',
    route: '/producao',
    permission: 'production:read',
    intro:
      'Ordens de produção com materiais previstos pela ficha técnica, reservas, consumo, perdas, entrada de acabados, apontamentos e qualidade.',
    topics: [
      {
        title: 'Criar e conduzir uma ordem de produção (OP)',
        steps: [
          'Em Produção › Ordens de produção, clique em Nova ordem (ou gere a OP a partir de um item do pedido de venda).',
          'Escolha produto, quantidade, datas e a versão da ficha técnica; os materiais previstos são calculados e podem ser ajustados.',
          'Avance o status: rascunho → planejada → liberada (reserva os materiais) → em produção (pode pausar e retomar) → concluída.',
          'Durante a produção registre consumos (baixam a reserva), perdas (com baixa opcional) e apontamentos de tempo por colaborador, equipe, turno e máquina.',
          'Ao concluir, lance a entrada do produto acabado; o custo unitário é apurado a partir do que foi consumido e apontado.',
        ],
        notes: [
          'A OP mostra custo industrial previsto × realizado.',
          'Cancelar exige motivo e libera as reservas.',
        ],
      },
      {
        title: 'Apontamentos',
        text: [
          'Produção › Apontamentos lista a produção por colaborador, equipe, turno e máquina. Os lançamentos são feitos na página de cada OP e alimentam a produtividade por colaborador no RH.',
        ],
      },
      {
        title: 'Qualidade e rastreabilidade',
        text: [
          'Em Produção › Qualidade registre inspeções (quantidade inspecionada, aprovada e rejeitada, motivos e evidências), com opção de lançar a entrada do acabado aprovado, e tratamentos fitossanitários por lote (prestador, certificado, resultado e custo).',
        ],
        notes: ['Nenhum tratamento ou certificação é presumido: só existe o que foi registrado.'],
      },
    ],
  },
  {
    id: 'clientes-fornecedores',
    title: 'Clientes e fornecedores',
    route: '/clientes',
    permission: 'customers:read / suppliers:read',
    intro:
      'Cadastros de parceiros com documentos validados, contatos, endereços e condições comerciais.',
    topics: [
      {
        title: 'Cadastrar um cliente ou fornecedor',
        steps: [
          'Abra Clientes (ou Fornecedores) e clique em Novo.',
          'Escolha pessoa física ou jurídica e digite o CPF/CNPJ: o sistema valida e, para CNPJ, consulta a Receita e preenche razão social, nome fantasia, telefone, e-mail e endereço (só os campos ainda vazios), avisando se a situação cadastral não estiver ativa. Nos endereços, digitar o CEP completa logradouro, bairro, cidade e UF.',
          'Digite o CEP dos endereços para preencher rua, bairro e cidade. Marque o endereço padrão, de cobrança e de entrega.',
          'Adicione contatos, inscrições, condições comerciais e limite de crédito. Em fornecedores, informe os materiais fornecidos.',
        ],
        notes: [
          'CPF/CNPJ é único por empresa. Os códigos CLI-000001 e FOR-000001 são automáticos.',
          'Inative em vez de excluir quando houver histórico.',
        ],
      },
    ],
  },
  {
    id: 'vendas',
    title: 'Vendas',
    route: '/vendas/orcamentos',
    permission: 'sales:read',
    intro: 'Orçamentos, pedidos de venda e solicitações vindas do site.',
    topics: [
      {
        title: 'Orçamento',
        steps: [
          'Em Vendas › Orçamentos, clique em Novo orçamento e escolha o cliente.',
          'Adicione itens (produtos do catálogo ou personalizados com especificações), descontos por item e geral, frete, impostos, validade, prazo de produção e condições de pagamento.',
          'Em produtos vendidos por m³, o item ganha os campos comprimento, largura e espessura (mm) e o preço por m³ (sugerido pelo cadastro, editável para o preço negociado com o cliente); o unitário, o volume e o total são calculados na hora. O PDF mostra as medidas, o volume e o preço por m³.',
          'Salve como rascunho, marque como Enviado e gere o PDF para o cliente.',
          'Registre a resposta: Aprovado, Recusado ou deixe expirar. Orçamentos aprovados podem ser convertidos em pedido.',
        ],
      },
      {
        title: 'Pedido de venda',
        steps: [
          'Crie o pedido diretamente ou converta um orçamento aprovado.',
          'Ao Confirmar, o sistema reserva os produtos acabados disponíveis (parcialmente, se o estoque não cobrir) e permite gerar ordens de produção por item.',
          'Acompanhe o status até a entrega e o faturamento; as contas a receber são geradas uma única vez em Financeiro.',
          'No pedido, o botão PDF gera o documento com o timbre da empresa para enviar ao cliente ou imprimir: dados do cliente, valor total, datas e situação, itens (com medidas, volume e preço por m³ nos vendidos por cubagem), descontos, frete, impostos, valor por extenso, condições comerciais, observações e campos de assinatura. Rascunhos e pedidos cancelados saem identificados.',
        ],
        notes: [
          'Cancelar um pedido libera as reservas; não é possível cancelar com OP ativa.',
          'A expedição é feita em Logística; o pedido passa a entregue parcial/total conforme as entregas confirmadas.',
        ],
      },
      {
        title: 'Solicitações do site',
        text: [
          'Pedidos de orçamento e contatos enviados pelo site institucional chegam em Site › Solicitações (também acessível por Vendas). Trate cada solicitação, vincule a um orçamento e acompanhe a conversão.',
        ],
      },
    ],
  },
  {
    id: 'compras',
    title: 'Compras',
    route: '/compras/pedidos',
    permission: 'purchases:read',
    intro:
      'Solicitações e pedidos de compra; o recebimento dá entrada no estoque ao custo efetivo.',
    topics: [
      {
        title: 'Fluxo de compra',
        steps: [
          'Em Compras › Pedidos de compra, clique em Nova solicitação, escolha o fornecedor e os materiais com quantidade e preço.',
          'Envie para aprovação; quem tem a permissão de aprovar confirma o pedido.',
          'No recebimento (parcial ou total) informe as quantidades e a nota fiscal: o estoque recebe a entrada ao custo efetivo e o último preço do fornecedor é atualizado.',
          'Gere as contas a pagar uma única vez a partir do pedido aprovado/recebido.',
        ],
      },
    ],
  },
  {
    id: 'financeiro',
    title: 'Financeiro',
    route: '/financeiro',
    permission: 'financial:read',
    intro:
      'Contas financeiras, entradas e saídas, contas a pagar e a receber, impostos, fluxo de caixa e DRE gerencial. A regra central: o título é a obrigação; o pagamento ou recebimento é a liquidação, que gera o movimento de caixa.',
    topics: [
      {
        title: 'Contas financeiras',
        text: [
          'Em Financeiro › Contas cadastre caixa, bancos e contas correntes com saldo inicial e data de abertura. O saldo atual é sempre calculado a partir dos lançamentos. Marque uma conta como padrão para agilizar as liquidações.',
        ],
      },
      {
        title: 'Contas a pagar e a receber',
        steps: [
          'Em Contas a pagar (ou a receber), clique em Novo título.',
          'Informe descrição, categoria, centro de custo, parceiro, valor total, competência, vencimento da 1ª parcela, número de parcelas e intervalo em dias.',
          'Anexe o boleto (PDF ou imagem) já no cadastro: no parcelamento, cada parcela tem o seu boleto. Depois, o boleto pode ser baixado, substituído ou removido no detalhe do título.',
          'Para pagar/receber, use a ação Pagar/Receber: escolha a conta, a data, o valor (parcial ou total), juros, multa, desconto e a forma de pagamento, e anexe o comprovante. O comprovante também pode ser anexado depois, em cada liquidação do detalhe do título.',
          'Precisou desfazer? Abra o detalhe do título e clique em Estornar na liquidação: o caixa recebe o movimento inverso e o título reabre.',
          'Para conferência do dia, clique em Imprimir / PDF em Contas a pagar: o relatório usa os filtros da tela (situação, vencimento, busca), traz o timbre da empresa (logo, razão social, CNPJ e endereço) no cabeçalho e no rodapé de cada página, agrupa os títulos por vencimento com subtotais e tem uma caixa de conferência por linha. Para conferir só algumas contas, marque-as na tabela (a marcação vale entre páginas) e clique em Imprimir conferência.',
          'Em Contas a receber, Exportar › PDF gera a posição dos títulos agrupada por cliente: total em aberto, total vencido, quantidade de títulos e clientes, e para cada título o vencimento com a situação (vencido há N dias, vence hoje ou a vencer), o valor original e o valor em aberto, com subtotal por cliente e total geral.',
        ],
        notes: [
          'Títulos também são gerados por pedidos de venda, pedidos de compra, notas fiscais, fretes de entrega e serviços de manutenção, sempre uma única vez por origem.',
          'O valor de um título fica travado após liquidações. Cancelamento exige motivo e ausência de liquidações ativas.',
          'A liquidação não aceita data futura nem valor acima do saldo em aberto.',
          'Parcelamento antigo: no cadastro, informe a Parcela inicial (a primeira ainda a pagar). As parcelas anteriores são geradas como Pagas antes do sistema: não movimentam o caixa e ficam fora dos totais, indicadores e DRE. Para um parcelamento já cadastrado, use Parcelas anteriores no detalhe de qualquer parcela.',
          'Boletos e comprovantes ficam guardados também em Documentos, vinculados ao título, e não podem ser excluídos por lá enquanto estiverem vinculados.',
        ],
      },
      {
        title: 'Impostos e parcelamentos',
        steps: [
          'Em Financeiro › Impostos, clique em Novo imposto.',
          'Escolha o tributo (DAS, ICMS, ISS, INSS, FGTS, IRPJ…), o período de apuração e a descrição.',
          'Informe o valor total, o primeiro vencimento e o número de parcelas mensais, ou marque Informar parcelas manualmente para digitar datas, valores e guias diferentes (parcelamentos como REFIS/PERT; marque Parcelamento formal e informe o número no órgão). Para parcelamentos longos, use Importar CSV com as colunas vencimento;valor (e opcionalmente guia), datas dd/mm/aaaa e valores como 1.234,56: as parcelas são preenchidas na tela e a parcela inicial é sugerida pela primeira com vencimento a partir de hoje. Limite de 240 parcelas.',
          'Anexe a guia/boleto de cada parcela no próprio cadastro ou, depois, no detalhe do imposto.',
          'Parcelamento antigo? Informe a Parcela inicial: as anteriores ficam como Pagas antes do sistema, sem movimento de caixa e fora dos totais. Em um imposto já cadastrado, use Parcelas anteriores no detalhe.',
          'Cada parcela vira uma conta a pagar. Pague pela ação Pagar na parcela, no detalhe do imposto ou na aba Agenda de pagamentos, anexando o comprovante.',
        ],
        notes: [
          'Os indicadores mostram em aberto, vencidos, próximos 30 dias, pago no mês e parcelamentos ativos; a agenda agrupa as parcelas por mês de vencimento.',
          'A situação do imposto (em aberto, parcialmente pago, pago) é consolidada automaticamente a partir das parcelas.',
        ],
      },
      {
        title: 'Empréstimos e financiamentos',
        steps: [
          'Em Financeiro › Empréstimos, clique em Novo empréstimo.',
          'Escolha o tipo: Tomado (a empresa recebe o dinheiro e paga as parcelas) ou Concedido (a empresa empresta e recebe). Informe credor/devedor, número do contrato, data e valor contratado.',
          'Escolha o sistema de amortização. Price (parcelas fixas) e SAC (amortização constante) calculam o cronograma a partir da taxa de juros ao mês, do número de parcelas e do primeiro vencimento, com prévia de amortização, juros e saldo devedor. Cronograma próprio aceita parcelas digitadas ou Importar CSV com as colunas vencimento;valor e, opcionalmente, amortização ou juros (datas dd/mm/aaaa, valores 1.234,56). Limite de 360 parcelas.',
          'Marque Registrar a entrada do valor na conta para lançar a liberação no caixa na data do contrato. Deixe desmarcado em contratos antigos, cujo valor já está no saldo.',
          'Contrato antigo? Informe a Parcela inicial: as anteriores ficam como Quitadas antes do sistema, sem caixa e fora dos totais. Ao importar o CSV, ela é sugerida pela primeira parcela com vencimento a partir de hoje.',
          'Cada parcela vira uma conta a pagar (tomado) ou a receber (concedido). Pague ou receba pela ação na parcela, no detalhe do empréstimo, ou em Contas a pagar/receber.',
        ],
        notes: [
          'Só os juros entram no resultado (DRE e indicadores), na categoria Empréstimos e financiamentos. A amortização do principal e a liberação do valor movimentam apenas o caixa. Se o cronograma não informar amortização/juros, a parcela inteira entra no DRE.',
          'O detalhe mostra valor contratado, total das parcelas, pago, em aberto, saldo devedor (principal ainda não amortizado) e juros.',
          'Cancelar um empréstimo exige motivo e ausência de pagamentos; as parcelas em aberto são canceladas e a liberação registrada no caixa é estornada.',
        ],
      },
      {
        title: 'Entradas, saídas e transferências',
        text: [
          'Financeiro › Entradas e Saídas listam todos os movimentos de caixa/banco: liquidações, lançamentos avulsos (sem título) e transferências. Lance um movimento avulso com Nova entrada/saída; transfira entre contas com Transferência; marque os movimentos conferidos com o extrato usando a conciliação.',
        ],
        notes: ['Movimentos ligados a liquidações só se estornam pela liquidação de origem.'],
      },
      {
        title: 'Fluxo de caixa e DRE',
        text: [
          'Fluxo de caixa mostra o realizado (lançamentos efetivos) e o projetado (títulos em aberto por vencimento), por dia ou mês, por conta ou consolidado. A Visão geral traz a DRE gerencial por competência com comparativo de caixa e o resultado do mês.',
        ],
      },
      {
        title: 'Categorias e centros de custo',
        text: [
          'Ficam em Configurações › Financeiro. Categorias são separadas em receitas e despesas; as de sistema (ex.: Impostos, Salários) não podem ser excluídas. Centros de custo (PROD, ADM, COM, LOG, MAN…) permitem analisar despesas por área.',
        ],
      },
    ],
  },
  {
    id: 'rh',
    title: 'RH',
    route: '/rh',
    permission: 'hr:read',
    intro:
      'Colaboradores, lançamentos (adiantamentos, vales, bonificações, descontos), férias e afastamentos e produtividade. Apoio gerencial: não substitui a folha legal nem o eSocial.',
    topics: [
      {
        title: 'Colaboradores',
        steps: [
          'Em RH › Colaboradores, clique em Novo colaborador e informe dados pessoais, departamento, cargo, vínculo, jornada e data de admissão.',
          'Dados sensíveis (CPF, endereço, salário, dados bancários) só são visíveis e editáveis por quem tem a permissão de RH sensível; cada acesso é auditado.',
          'Altere o salário sempre pelo histórico salarial: o salário vigente é o registro de maior vigência até hoje.',
          'Desligue (nunca exclua) colaboradores que saíram; o histórico de apontamentos e lançamentos é preservado.',
        ],
      },
      {
        title: 'Lançamentos, adiantamentos e vales',
        steps: [
          'Crie o lançamento (adiantamento, vale, bonificação, hora extra, benefício ou desconto) para o colaborador e a competência.',
          'Autorize o lançamento.',
          'Ao pagar, escolha a conta financeira: o caixa recebe um único movimento.',
          'Marque como descontado em folha quando for compensado. O cancelamento estorna o caixa.',
        ],
        notes: [
          'O demonstrativo mensal por competência resume tudo o que foi lançado para cada colaborador.',
        ],
      },
      {
        title: 'Recibos para assinatura',
        steps: [
          'Em Lançamentos (ou Adiantamentos/Vales), filtre o que deseja e clique em Imprimir recibos: sai um recibo por lançamento, 4 por folha A4, com linha de recorte, para imprimir todos de uma vez.',
          'Para imprimir só alguns, marque as linhas na tabela (a marcação vale entre páginas) e use Imprimir recibos dos marcados.',
        ],
        notes: [
          'Cada recibo traz a empresa, o valor em destaque e por extenso, o motivo, a autorização de desconto em folha (adiantamentos), cidade e data do pagamento (ou do lançamento, se a data de pagamento não foi informada) e a linha de assinatura com nome e matrícula.',
          'Para ajustar a data que sai no recibo, use o menu ⋯ do lançamento › Informar/Alterar data de pagamento. Antes de pagar pelo caixa, pode ser a data prevista; depois de pago, a saída no financeiro acompanha a nova data (não pode ser futura nem estar conciliada). A coluna Pagamento da lista e a exportação mostram essa data.',
          'O CPF do colaborador só aparece para quem tem acesso a dados sensíveis de RH; toda impressão fica registrada na Auditoria.',
          'Descontos e lançamentos cancelados não geram recibo. Até 600 recibos por impressão.',
        ],
      },
      {
        title: 'Comprovantes dos lançamentos',
        steps: [
          'Ao pagar pelo caixa, use Anexar comprovante no próprio diálogo para guardar o PDF ou a imagem da transferência/PIX.',
          'Depois, na coluna Comprovante da lista de lançamentos (ou na aba Lançamentos do colaborador), use Anexar para incluir o recibo assinado, a nota do vale ou qualquer outro comprovante, em qualquer situação exceto cancelado.',
          'Ver baixa o arquivo; o clipe substitui por um novo arquivo e a lixeira remove.',
        ],
        notes: [
          'Cada lançamento guarda um comprovante; ao substituir, o arquivo anterior fica no histórico de versões.',
          'O comprovante também aparece em Documentos, vinculado ao colaborador, e não pode ser excluído por lá enquanto estiver ligado ao lançamento. Anexos, remoções e downloads ficam registrados na Auditoria.',
        ],
      },
      {
        title: 'Férias e afastamentos',
        text: [
          'Registre férias, atestados, licenças e outros afastamentos sem sobreposição de períodos. A situação do colaborador (ativo, em férias, afastado) acompanha o período vigente.',
        ],
      },
    ],
  },
  {
    id: 'patrimonio',
    title: 'Patrimônio',
    route: '/patrimonio',
    permission: 'assets:read',
    intro:
      'Equipamentos e veículos com código patrimonial, responsável, depreciação gerencial, documentos e alertas de seguro e licenciamento.',
    topics: [
      {
        title: 'Cadastrar um ativo',
        steps: [
          'Em Patrimônio › Equipamentos (ou Veículos), clique em Novo ativo.',
          'Informe categoria (caminhão, empilhadeira, serra, pregadeira…), identificação, responsável, estado, valores de aquisição e residual e vida útil: o valor contábil gerencial é calculado por depreciação linear.',
          'Para veículos, preencha placa, RENAVAM, quilometragem, combustível e vencimentos de seguro e licenciamento (geram alertas).',
        ],
        notes: [
          'Situações: ativo, em manutenção, inativo, vendido e baixado. A visão geral resume valores e custos de manutenção por categoria.',
          'Os planos de manutenção preventiva são cadastrados na página de cada ativo.',
        ],
      },
    ],
  },
  {
    id: 'manutencoes',
    title: 'Manutenções',
    route: '/manutencoes/ordens',
    permission: 'maintenance:read',
    intro:
      'Planos preventivos por data, quilometragem ou horas e ordens de manutenção preventivas e corretivas.',
    topics: [
      {
        title: 'Ordem de manutenção (OM)',
        steps: [
          'Em Manutenções › Ordens, clique em Nova ordem e escolha o ativo e o tipo (preventiva ou corretiva).',
          'Avance o ciclo: aberta → programada → em andamento → aguardando peças → concluída (ou cancelada).',
          'Registre as peças consumidas do estoque (saem ao custo médio; devoluções são possíveis), o responsável ou prestador, o custo do serviço e o tempo parado.',
          'Ao concluir, atualize km/horímetro: o plano preventivo é reprogramado e, se houver serviço terceirizado, gere a conta a pagar (uma única vez).',
        ],
      },
      {
        title: 'Preventivas',
        text: [
          'Manutenções › Preventivas lista todos os planos com o próximo vencimento calculado. Planos vencidos aparecem nos alertas.',
        ],
      },
    ],
  },
  {
    id: 'horas-maquina',
    title: 'Horas de máquina',
    route: '/horas-maquina',
    permission: 'machines:read',
    intro:
      'Controle o uso das máquinas pelos clientes: saída e retorno, horas apuradas e cobradas, valor da hora, combustível e a fatura com o extrato de consolidação para cobrança.',
    topics: [
      {
        title: 'Registrar a saída e o retorno de uma máquina',
        steps: [
          'Em Horas de máquina › Apontamentos, clique em Novo apontamento.',
          'Escolha o cliente e a máquina (bens cadastrados em Patrimônio). O valor da hora é sugerido a partir do último apontamento da máquina.',
          'Informe a data e a hora da saída. Enquanto a máquina estiver com o cliente, deixe o retorno em branco: o apontamento fica Em uso.',
          'Quando a máquina voltar, abra o apontamento e clique em Registrar retorno: informe data e hora do retorno. As horas são apuradas automaticamente (retorno − saída) e o apontamento passa para A faturar.',
          'Opcional: horímetro na saída e no retorno (atualiza o horímetro da máquina, usado nas preventivas por horas), operador e local/obra.',
        ],
        notes: [
          'A mesma máquina não pode estar apontada para dois clientes no mesmo período.',
          'A prévia no rodapé do formulário mostra horas cobradas, valor das horas, combustível e o total a cobrar.',
        ],
      },
      {
        title: 'Corrigir as horas cobradas',
        text: [
          'Se a máquina teve intervalos de uso (parada, chuva, almoço) ou você quer dar desconto, marque Corrigir as horas cobradas manualmente e informe as horas que serão cobradas (8,5 = 8h30) e o motivo. As horas apuradas continuam registradas; o extrato da fatura mostra as duas e o motivo do ajuste.',
        ],
      },
      {
        title: 'Combustível',
        steps: [
          'Informe os litros no tanque na saída e no retorno.',
          'Em Abastecimentos durante o uso, adicione cada abastecimento: data e hora, litros, preço pago por litro (opcional) e quem pagou — a empresa ou o próprio cliente.',
          'Para cobrar o combustível, marque Cobrar do cliente o combustível consumido e informe o preço do litro cobrado.',
        ],
        notes: [
          'Consumo = tanque na saída + abastecimentos − tanque no retorno.',
          'Combustível cobrado = tanque na saída + abastecimentos pagos pela empresa − tanque no retorno (nunca negativo), multiplicado pelo preço do litro. Abastecimentos pagos pelo cliente contam no consumo, mas não na cobrança.',
          'O detalhe do apontamento mostra também o gasto da empresa com os abastecimentos (litros × preço pago).',
        ],
      },
      {
        title: 'Emitir a fatura com extrato de consolidação',
        steps: [
          'Na lista de apontamentos, marque os apontamentos A faturar de um mesmo cliente e clique em Emitir fatura dos marcados (ou use Emitir fatura no menu de um apontamento).',
          'Confira os apontamentos, informe emissão, vencimento, desconto (opcional) e observações (ex.: dados para pagamento) e clique em Emitir fatura.',
          'A fatura gera uma conta a receber do cliente (categoria Locação de máquinas) e os apontamentos passam para Faturado.',
          'Em Horas de máquina › Faturas, use PDF da fatura para imprimir ou enviar ao cliente: o PDF traz os dados do cliente, o total a pagar e o vencimento, o extrato de consolidação (saída, retorno, horas apuradas e cobradas, valor da hora, combustível e total de cada apontamento), o detalhamento do combustível e dos abastecimentos, o desconto e o valor por extenso.',
          'Registre o recebimento pela ação Receber da fatura ou em Financeiro › Contas a receber.',
        ],
        notes: [
          'Apontamentos faturados ficam bloqueados para edição. Para corrigir, cancele a fatura (exige motivo e nenhum recebimento): a conta a receber é cancelada e os apontamentos voltam para A faturar.',
          'Emitir e cancelar faturas exige a permissão Horas de máquina — Aprovar; receber exige Financeiro — Aprovar.',
        ],
      },
    ],
  },
  {
    id: 'mtr',
    title: 'MTR (resíduos)',
    route: '/mtr',
    permission: 'waste:read',
    intro:
      'Registre os Manifestos de Transporte de Resíduos (MTR) emitidos no SINIR ou no sistema estadual: resíduos e quantidades, transportador, destinador, recebimento e o Certificado de Destinação Final (CDF), com os PDFs anexados.',
    topics: [
      {
        title: 'Cadastrar um MTR',
        steps: [
          'Em MTR, clique em Novo MTR.',
          'Informe o número do MTR (se já emitido), o sistema emissor e as datas de emissão e expedição.',
          'Em Resíduos, use as sugestões (serragem, descasque, embalagens de madeira) ou preencha descrição, classe (NBR 10004), quantidade e unidade. Acondicionamento e tratamento têm sugestões ao digitar. Confira o código com o informado no sistema do MTR.',
          'Preencha transportador (nome, CNPJ, placa, motorista) e destinador (nome, CNPJ, licença ambiental) e clique em Cadastrar MTR. Ele fica como Rascunho.',
        ],
      },
      {
        title: 'Acompanhar: emitido, recebido e CDF',
        steps: [
          'No MTR, clique em Marcar como emitido e confirme o número oficial (obrigatório e único) e a data da expedição.',
          'Quando o destinador confirmar, clique em Registrar recebimento: informe a data e, se já tiver, o número e a data do CDF. O CDF também pode ser informado depois, editando o MTR.',
          'Em Arquivos, anexe o PDF do MTR e do CDF. Eles ficam também em Documentos, como documentos ambientais vinculados ao MTR.',
          'Na lista, os indicadores mostram os emitidos e a quantidade do mês, os que aguardam recebimento e os recebidos sem CDF (com atalho para filtrar). Exportar gera PDF, CSV ou Excel com os filtros da tela.',
        ],
        notes: [
          'Rascunhos e emitidos podem ser cancelados (com motivo); cancele também no sistema oficial. MTR recebido não é cancelado e MTR cancelado não é editado.',
          'Cadastrar e editar exigem as permissões Resíduos (MTR) — Criar/Editar; cancelar exige Excluir. Os perfis Produção e Expedição já têm acesso; Estoque consulta.',
        ],
      },
    ],
  },
  {
    id: 'notas-fiscais',
    title: 'Notas fiscais',
    route: '/notas-fiscais',
    permission: 'invoices:read',
    intro:
      'Registro manual, importação de XML e recebimento automático pela SEFAZ (certificado digital) de NF-e de entrada e saída, vinculadas a parceiros, pedidos, estoque e financeiro. A plataforma não emite notas.',
    topics: [
      {
        title: 'Importar um XML',
        steps: [
          'Em Notas fiscais, clique em Nova nota e escolha Importar XML.',
          'O sistema valida a estrutura, extrai chave, emitente, destinatário, itens, tributos e totais, sugere a direção pelo CNPJ da empresa e detecta duplicidade.',
          'Confirme os vínculos sugeridos: fornecedor/cliente pelo documento e pedido de compra pelo recebimento. Associe os itens a materiais/produtos.',
          'Use Lançar estoque para dar entrada/saída (uma única vez; bloqueado se o pedido já movimentou) e Gerar títulos para criar as contas a pagar/receber (uma única vez).',
        ],
        notes: ['XML e DANFE ficam guardados em Documentos.'],
      },
      {
        title: 'Receber NF-e automaticamente da SEFAZ',
        steps: [
          'Em Configurações → Empresa, envie o certificado digital A1 (.pfx) com a senha. Confira o CNPJ e a UF do endereço da empresa: a SEFAZ usa os dois na consulta.',
          'Abra Notas fiscais → SEFAZ. A cada hora o sistema consulta a SEFAZ e lista as NF-e emitidas contra o CNPJ da empresa; use Sincronizar agora para não esperar.',
          'Com Ciência automática ligada, o sistema manifesta Ciência da Operação para cada NF-e nova e, na sequência, recebe o XML completo. Com Importação automática, a nota de entrada é criada sozinha, já vinculada ao fornecedor pelo CNPJ e com o XML guardado em Documentos.',
          'Nas notas recebidas, confirme a operação, registre desconhecimento ou operação não realizada (com justificativa), baixe o XML, importe manualmente ou marque como ignorada.',
        ],
        notes: [
          'A SEFAZ só permite uma nova consulta sem documentos novos a cada hora; a tela mostra quando a próxima será possível.',
          'O certificado e a senha ficam cifrados no servidor (chave FISCAL_CERT_KEY) e nunca são exibidos. Enviar e remover exigem Configurações — Gerenciar; sincronizar e importar seguem as permissões de Notas fiscais.',
          'Notas canceladas ou denegadas pela SEFAZ aparecem com a situação correspondente e não são importadas.',
        ],
      },
      {
        title: 'Registro manual',
        text: [
          'Para notas sem XML, preencha os dados principais e os itens; os mesmos vínculos e lançamentos ficam disponíveis.',
        ],
      },
      {
        title: 'Editar e vincular cliente ou fornecedor',
        steps: [
          'No detalhe da nota, clique em Editar.',
          'Escolha o cliente (nota de saída) ou o fornecedor (nota de entrada). O cadastro com o mesmo CNPJ/CPF da nota aparece marcado; se ainda não existir, use Cadastre o cliente/fornecedor (abre em nova aba) e reabra a edição.',
          'Ajuste também situação, protocolo e observações. Em notas registradas manualmente dá para corrigir número, série, emissão, emitente e destinatário; nas importadas esses dados seguem o XML.',
        ],
        notes: [
          'Se a nota já gerou títulos, eles passam para o nome do cliente/fornecedor vinculado.',
        ],
      },
      {
        title: 'Excluir uma nota lançada por engano',
        steps: [
          'No detalhe da nota, clique em Excluir e confirme. A nota, os itens e os arquivos XML/DANFE são apagados definitivamente; uma nota importada pode ser importada de novo.',
        ],
        notes: [
          'Só é possível sem estoque aplicado e sem títulos ativos: estorne os movimentos e cancele os títulos antes. A exclusão fica registrada na Auditoria.',
          'Para manter o histórico (nota real que deixou de valer), prefira Cancelar.',
        ],
      },
    ],
  },
  {
    id: 'documentos',
    title: 'Documentos',
    route: '/documentos',
    permission: 'documents:read',
    intro:
      'Contratos, licenças, certificados e documentos de veículos, colaboradores e máquinas, com versões, validade e download auditado.',
    topics: [
      {
        title: 'Enviar um documento',
        steps: [
          'Em Documentos, clique em Enviar documento e escolha o arquivo.',
          'Informe tipo, tags, datas de emissão e validade e o vínculo (cliente, fornecedor, colaborador, ativo, pedido, nota, entrega…).',
          'Marque Confidencial para restringir o acesso a quem gerencia documentos ou tem RH sensível.',
        ],
        notes: [
          'Enviar um novo arquivo para o mesmo documento cria uma nova versão; as anteriores continuam disponíveis.',
          'Documentos com validade próxima geram alertas.',
        ],
      },
    ],
  },
  {
    id: 'logistica',
    title: 'Logística',
    route: '/logistica',
    permission: 'logistics:read',
    intro:
      'Separação de pedidos, romaneios, expedição e confirmação de entregas integradas ao estoque, veículos e financeiro.',
    topics: [
      {
        title: 'Da separação à entrega',
        steps: [
          'Em Logística › Separação veja o saldo a expedir de cada item dos pedidos confirmados e monte a entrega por cliente (endereço de entrega, motorista, veículo, transportadora e frete).',
          'Expedir baixa o estoque de acabados consumindo as reservas do pedido.',
          'Confirme a entrega informando as quantidades efetivas: o pedido passa a entregue parcial/total e a quilometragem do veículo é atualizada. Anexe o comprovante como documento.',
          'Frete terceirizado gera uma conta a pagar (uma única vez).',
        ],
        notes: ['Cancelar uma entrega já expedida devolve o estoque.'],
      },
    ],
  },
  {
    id: 'alertas',
    title: 'Alertas',
    route: '/alertas',
    permission: 'dashboard:read',
    intro:
      'Pendências calculadas em tempo real e filtradas pelas suas permissões: documentos vencendo, seguro e licenciamento de veículos, preventivas vencidas, manutenções e entregas atrasadas, títulos vencidos, notas de entrada sem estoque e lançamentos de RH pendentes.',
    topics: [
      {
        title: 'Onde ver',
        text: [
          'O sino no cabeçalho mostra os alertas mais recentes com o total (vermelho quando há críticos). A página Alertas lista todos com link direto para o registro.',
        ],
      },
    ],
  },
  {
    id: 'site',
    title: 'Site institucional',
    route: '/site',
    permission: 'settings:read / products:read / sales:read',
    intro:
      'Tudo o que o site público exibe vem daqui: conteúdo, catálogo e as solicitações que ele gera.',
    topics: [
      {
        title: 'Conteúdo',
        text: [
          'Em Site › Conteúdo edite o texto do hero, a apresentação, missão/visão/valores, diferenciais, WhatsApp, telefone, e-mail, endereço, horário, Instagram e o aviso do formulário de orçamento. As alterações entram no ar em alguns minutos (cache).',
        ],
      },
      {
        title: 'Catálogo',
        text: [
          'Em Site › Catálogo escolha quais produtos ativos aparecem no site, a ordem e a descrição comercial. Preços e estoque nunca são publicados.',
        ],
      },
      {
        title: 'Solicitações',
        text: [
          'Orçamentos e contatos enviados pelo site chegam em Site › Solicitações. Marque como contatado, vincule a um orçamento ou descarte; a visão geral mostra conversões e pendências.',
        ],
      },
    ],
  },
  {
    id: 'relatorios',
    title: 'Relatórios',
    route: '/relatorios',
    permission: 'reports:read',
    intro:
      'Central de relatórios gerenciais com filtros por período e dimensões, exportáveis em CSV, Excel e PDF. Só aparecem os relatórios dos módulos que você pode ler.',
    topics: [
      {
        title: 'Relatórios disponíveis',
        text: [
          'Produção por período e por produto, custo por palete, consumo de madeira e insumos, perdas, produtividade, vendas por cliente, vendas e rentabilidade por produto, compras por fornecedor, estoque valorizado, contas a pagar, contas a receber, fluxo de caixa, despesas por categoria, gastos por máquina/veículo, manutenções realizadas, patrimônio e quadro de colaboradores (salário só com RH sensível).',
        ],
        steps: [
          'Abra Relatórios e escolha o relatório.',
          'Ajuste os filtros (período, cliente, produto, material, fornecedor, equipamento, depósito, centro de custo, situação) e clique em Gerar.',
          'Exporte em CSV, Excel ou PDF; cada exportação fica registrada na auditoria.',
        ],
      },
    ],
  },
  {
    id: 'configuracoes',
    title: 'Configurações',
    route: '/configuracoes',
    permission: 'settings:read / users:read / roles:read / audit:read',
    intro:
      'Administração da empresa ativa: dados cadastrais, cadastros auxiliares, financeiro, usuários, perfis, permissões e auditoria.',
    topics: [
      {
        title: 'Empresa',
        text: [
          'Razão social, nome fantasia, CNPJ (preenchido pela Receita), endereço, contatos, logotipo e cor da marca, além das configurações regionais. O conteúdo do site fica no menu Site.',
        ],
      },
      {
        title: 'Cadastros auxiliares',
        text: [
          'Unidades de medida, categorias de produto e material e depósitos (com um padrão). Itens em uso não podem ser excluídos.',
        ],
      },
      {
        title: 'Usuários',
        steps: [
          'Em Configurações › Usuários, clique em Novo usuário e informe nome, e-mail e senha inicial.',
          'Atribua um ou mais perfis (administrador, gerente, financeiro, RH, produção, estoque, compras, vendas, manutenção, expedição, visualizador).',
          'Para redefinir a senha de alguém ou desativar o acesso, use as ações da linha.',
        ],
      },
      {
        title: 'Perfis e permissões',
        text: [
          'Cada perfil reúne permissões por módulo e ação: visualizar, criar, editar, excluir/desativar, aprovar, exportar e gerenciar configurações. Os perfis do sistema podem ter permissões ajustadas, exceto o administrador, que sempre tem tudo. Crie perfis próprios quando precisar de combinações específicas.',
        ],
      },
      {
        title: 'Auditoria',
        text: [
          'Registro de todas as operações relevantes (quem, quando, o quê, de onde): logins, alterações, liquidações, estornos, exportações, acessos a dados sensíveis. Filtre por usuário, ação, período e registro.',
        ],
      },
    ],
  },
  {
    id: 'administracao',
    title: 'Administração global',
    route: '/admin/empresas',
    permission: 'superadministrador',
    intro:
      'Disponível apenas para o superadministrador: criação e gestão das empresas (tenants) hospedadas na plataforma.',
    topics: [
      {
        title: 'Empresas',
        text: [
          'Crie uma empresa informando nome, CNPJ e o primeiro administrador; ative ou suspenda o acesso; acompanhe uso. Cada empresa tem seus dados completamente isolados.',
        ],
      },
    ],
  },
  {
    id: 'duvidas',
    title: 'Dúvidas frequentes',
    intro: 'Respostas rápidas para as situações mais comuns.',
    topics: [
      {
        title: 'Não vejo um módulo no menu',
        text: [
          'Seu perfil não tem permissão de leitura nele. Peça ao administrador da empresa para ajustar o perfil em Configurações › Perfis e permissões.',
        ],
      },
      {
        title: 'Errei um pagamento',
        text: [
          'Abra o título em Contas a pagar/receber, localize a liquidação e clique em Estornar, informando o motivo. O caixa recebe o movimento inverso e o título reabre.',
        ],
      },
      {
        title: 'Quero excluir um registro com histórico',
        text: [
          'Use cancelar, desligar ou inativar. A exclusão é reservada a cadastros nunca utilizados, para preservar a rastreabilidade.',
        ],
      },
      {
        title: 'O estoque não deixa dar saída',
        text: [
          'O saldo disponível (saldo menos reservas) não cobre a quantidade. Verifique as reservas de pedidos e OPs ou peça a quem tem permissão de aprovação do estoque para registrar a exceção.',
        ],
      },
      {
        title: 'Como falar com o suporte',
        text: [
          'Use o botão Suporte no cabeçalho: além deste manual, ele traz os contatos configurados pela sua empresa (e-mail e WhatsApp).',
        ],
      },
    ],
  },
];
